import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SchemaOrg from '@/components/base/SchemaOrg';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://superbuyspread.hair';
const pagePath = '/article/superbuy-spreadsheet-for-beginners';

const articleSchema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline:
    'Superbuy Spreadsheet for Beginners: Your First 30 Days of Smarter Shopping',
  description:
    'Everything a first-time international shopper needs to know about setting up, using, and mastering the Superbuy Spreadsheet system — from downloading your first template to placing your first optimized order.',
  author: {
    '@type': 'Organization',
    name: 'SuperbuySpread.hair Editorial Team',
    url: siteUrl,
  },
  publisher: {
    '@type': 'Organization',
    name: 'SuperbuySpread.hair',
    url: siteUrl,
  },
  datePublished: '2026-07-13',
  dateModified: '2026-07-13',
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': `${siteUrl}${pagePath}`,
  },
  inLanguage: 'en',
};

export default function BeginnersGuidePage() {
  return (
    <>
      <SchemaOrg data={articleSchema} />
      <Navbar />
      <main>
        <article className="pt-24 md:pt-28 pb-10 md:pb-14">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <nav className="mb-6" aria-label="Breadcrumb">
              <ol className="flex items-center gap-2 text-xs md:text-sm text-foreground-400">
                <li>
                  <Link to="/" className="hover:text-primary-500 transition-colors duration-150">
                    Home
                  </Link>
                </li>
                <li className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line text-xs"></i>
                </li>
                <li className="text-foreground-600 font-medium">Beginner's Guide</li>
              </ol>
            </nav>

            <header className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-2.5 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full">
                  Getting Started
                </span>
                <span className="text-xs text-foreground-400">8 min read</span>
              </div>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4">
                Superbuy Spreadsheet for Beginners: Your First 30 Days of Smarter Shopping
              </h1>
              <p className="text-foreground-500 text-sm md:text-base leading-relaxed">
                Everything a first-time international shopper needs to know about setting up, using, and mastering the Superbuy Spreadsheet system — from downloading your first template to placing your first optimized order.
              </p>
            </header>

            <div className="w-full h-64 md:h-80 rounded-xl overflow-hidden mb-10">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20minimalist%20workspace%20photography%20of%20a%20clean%20desk%20setup%20with%20a%20laptop%20displaying%20a%20colorful%20spreadsheet%20next%20to%20a%20coffee%20cup%20and%20a%20small%20potted%20plant%20soft%20natural%20morning%20light%20streaming%20through%20sheer%20curtains%20editorial%20lifestyle%20aesthetic%20warm%20cream%20and%20sage%20tones%20organized%20and%20peaceful%20beginner%20friendly%20atmosphere&width=1200&height=600&seq=article-beginners-hero-02&orientation=landscape"
                alt="Superbuy Spreadsheet beginner's guide workspace setup"
                title="Superbuy Spreadsheet Beginners Guide - Getting Started with International Shopping"
                className="w-full h-full object-cover object-center"
              />
            </div>

            <div className="prose-custom text-foreground-800 text-sm md:text-base leading-relaxed space-y-6">
              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4">
                  Welcome: You Do Not Need to Be a Spreadsheet Expert
                </h2>
                <p>
                  Let us address the elephant in the room right away: if the word &ldquo;spreadsheet&rdquo; makes you think of intimidating rows of numbers and complex formulas you barely passed in school, breathe easy. The <strong>Superbuy Spreadsheet</strong> is designed for real people — not accountants, not data analysts, not spreadsheet wizards. If you can type a product name and a price into a cell, you already have all the technical skills you need.
                </p>
                <p>
                  The entire system is <em>pre-built</em>. When you open your first Superbuy Spreadsheet template, you will see labeled columns, color-coded sections, and formula cells that do the math automatically. Your job is simply to fill in the information about your purchases. The spreadsheet handles everything else: shipping estimates, budget tracking, order status monitoring, and cost comparisons.
                </p>
                <p>
                  Before diving in, you might want to explore our{' '}
                  <Link to="/article/superbuy-spreadsheet-complete-guide" className="text-primary-500 hover:text-primary-600 font-medium underline underline-offset-2">
                    complete Superbuy Spreadsheet guide
                  </Link>{' '}
                  for a deeper overview, or browse all categories on{' '}
                  <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium underline underline-offset-2">
                    the homepage
                  </Link>.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4">
                  Step 1: Download and Set Up Your First Template
                </h2>
                <p>
                  Your Superbuy Spreadsheet journey begins with downloading the starter template. We recommend Google Sheets for beginners because it is free, works on any device, saves automatically, and can be shared with family or business partners. If you prefer Microsoft Excel, the template exports seamlessly to .xlsx format.
                </p>

                <div className="bg-background-100 rounded-xl p-5 my-6">
                  <h4 className="font-semibold text-foreground-900 text-sm mb-3">Template Setup Checklist:</h4>
                  <ol className="list-decimal pl-5 space-y-2 text-xs text-foreground-600">
                    <li>Download the free Superbuy Spreadsheet Starter Pack from the main website</li>
                    <li>Open the file in Google Sheets or Excel</li>
                    <li>Make a copy (File → Make a Copy) so you always have the original template</li>
                    <li>Rename your copy to something memorable like &ldquo;My Shopping Dashboard 2026&rdquo;</li>
                    <li>Familiarize yourself with the four main tabs: Products, Orders, Shipping, and Budget</li>
                  </ol>
                </div>
              </section>

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4">
                  Step 2: Add Your First Five Products
                </h2>
                <p>
                  Do not overthink this step. The goal is to get data into the spreadsheet so you can see how it works. Open the <strong>Products</strong> tab and add five items you are interested in or have recently purchased. For each product, fill in:
                </p>
                <ul className="list-disc pl-5 space-y-2 text-sm text-foreground-600 my-4">
                  <li><strong>Product Name</strong>: Something descriptive you will recognize later</li>
                  <li><strong>Marketplace</strong>: Where you found it (e.g., Taobao, Weidian, 1688)</li>
                  <li><strong>URL</strong>: Direct link to the product page</li>
                  <li><strong>Price</strong>: In the original currency</li>
                  <li><strong>Estimated Weight</strong>: Your best guess — the seller's listing usually includes this</li>
                  <li><strong>Category</strong>: Pick from the dropdown (Shoes, Hoodies, T-Shirts, etc.)</li>
                </ul>
                <p>
                  Already the spreadsheet starts working for you. It auto-calculates the total value of your product database and flags any items where the price seems unusually high or low compared to your other entries — an early warning system for pricing outliers.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4">
                  Step 3: Understand the Dashboard at a Glance
                </h2>
                <p>
                  The Superbuy Spreadsheet dashboard is built around a simple principle: <strong>one glance should tell you everything you need to know</strong>. Here is what each section shows you:
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
                  <div className="rounded-xl bg-background-100 p-5">
                    <div className="w-8 h-8 rounded-lg bg-secondary-100 flex items-center justify-center mb-3">
                      <i className="ri-shopping-cart-line text-secondary-600"></i>
                    </div>
                    <h4 className="font-semibold text-foreground-900 text-sm mb-1">Products Tab</h4>
                    <p className="text-foreground-500 text-xs leading-relaxed">
                      Your complete wishlist and purchase history. Filter by category, sort by price, and see which items you have already ordered.
                    </p>
                  </div>
                  <div className="rounded-xl bg-background-100 p-5">
                    <div className="w-8 h-8 rounded-lg bg-secondary-100 flex items-center justify-center mb-3">
                      <i className="ri-truck-line text-secondary-600"></i>
                    </div>
                    <h4 className="font-semibold text-foreground-900 text-sm mb-1">Orders Tab</h4>
                    <p className="text-foreground-500 text-xs leading-relaxed">
                      Every order you place, from warehouse arrival to doorstep delivery. Color codes show status at a glance.
                    </p>
                  </div>
                  <div className="rounded-xl bg-background-100 p-5">
                    <div className="w-8 h-8 rounded-lg bg-secondary-100 flex items-center justify-center mb-3">
                      <i className="ri-ship-line text-secondary-600"></i>
                    </div>
                    <h4 className="font-semibold text-foreground-900 text-sm mb-1">Shipping Tab</h4>
                    <p className="text-foreground-500 text-xs leading-relaxed">
                      Estimated shipping costs for each item and combined orders. Compare carriers before you ship.
                    </p>
                  </div>
                  <div className="rounded-xl bg-background-100 p-5">
                    <div className="w-8 h-8 rounded-lg bg-secondary-100 flex items-center justify-center mb-3">
                      <i className="ri-wallet-line text-secondary-600"></i>
                    </div>
                    <h4 className="font-semibold text-foreground-900 text-sm mb-1">Budget Tab</h4>
                    <p className="text-foreground-500 text-xs leading-relaxed">
                      Monthly and quarterly spending summaries with a progress bar. Set your target and track automatically.
                    </p>
                  </div>
                </div>

                <p>
                  For those who want to dive deeper into shipping optimization specifically, we have a dedicated{' '}
                  <Link to="/article/superbuy-spreadsheet-shipping-guide" className="text-primary-500 hover:text-primary-600 font-medium underline underline-offset-2">
                    shipping cost reduction guide
                  </Link>{' '}
                  that covers carrier comparison and consolidation strategies in detail.
                </p>
              </section>

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4">
                  Common Beginner Mistakes and How to Avoid Them
                </h2>

                <div className="space-y-5 my-6">
                  <div className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-error-warning-line text-accent-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground-900 text-sm mb-1">Mistake: Not updating order statuses</h4>
                      <p className="text-foreground-500 text-xs leading-relaxed">
                        The spreadsheet only works if you keep it current. Set a weekly 5-minute reminder to update tracking numbers and delivery statuses. The habit forms quickly.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-error-warning-line text-accent-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground-900 text-sm mb-1">Mistake: Guessing shipping weights</h4>
                      <p className="text-foreground-500 text-xs leading-relaxed">
                        Take the extra 30 seconds to find the actual listed weight on the product page. A 200g estimation error on five items compounds into a kilogram difference — that can change your shipping cost by $15-$30.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-error-warning-line text-accent-600"></i>
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground-900 text-sm mb-1">Mistake: Starting with too many products</h4>
                      <p className="text-foreground-500 text-xs leading-relaxed">
                        Your first week, add 5-10 items max. Get comfortable with the workflow before scaling up. The spreadsheet becomes more valuable as your data grows — but only if the data is accurate.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4">
                  Your First 30 Days: A Week-by-Week Plan
                </h2>
                <div className="space-y-4 my-6">
                  <div className="rounded-xl bg-background-100 p-5">
                    <h4 className="font-semibold text-foreground-900 text-sm mb-2">Week 1: Setup and Exploration</h4>
                    <p className="text-foreground-500 text-xs leading-relaxed">
                      Download the template, add 5-10 products you are genuinely interested in, and spend 15 minutes clicking through each tab to understand the layout. Do not place any orders yet — just get comfortable.
                    </p>
                  </div>
                  <div className="rounded-xl bg-background-100 p-5">
                    <h4 className="font-semibold text-foreground-900 text-sm mb-2">Week 2: First Small Order</h4>
                    <p className="text-foreground-500 text-xs leading-relaxed">
                      Pick one product under $30 from your database. Use the shipping estimator to compare carriers, then place your order. Log the tracking number immediately. This low-stakes test run builds confidence.
                    </p>
                  </div>
                  <div className="rounded-xl bg-background-100 p-5">
                    <h4 className="font-semibold text-foreground-900 text-sm mb-2">Week 3: Multi-Item Experiment</h4>
                    <p className="text-foreground-500 text-xs leading-relaxed">
                      Add 3-5 new products and use the consolidation planner to see if combined shipping saves money. Place the order and track the actual cost versus the spreadsheet estimate.
                    </p>
                  </div>
                  <div className="rounded-xl bg-background-100 p-5">
                    <h4 className="font-semibold text-foreground-900 text-sm mb-2">Week 4: Budget Review</h4>
                    <p className="text-foreground-500 text-xs leading-relaxed">
                      Set a monthly budget in the Budget tab. Review all your orders, shipping costs, and total spend. Adjust your budget target based on real data. You now have a complete system.
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4">
                  Beginner-Friendly FAQ
                </h2>
                <div className="space-y-4 my-6">
                  <div className="rounded-xl bg-background-100 p-5">
                    <h4 className="font-semibold text-foreground-900 text-sm mb-2">
                      Do I need any paid tools or subscriptions?
                    </h4>
                    <p className="text-foreground-500 text-xs leading-relaxed">
                      No. The basic Superbuy Spreadsheet template works with free Google Sheets. You only need a Google account (also free). Premium features on the main website are optional.
                    </p>
                  </div>
                  <div className="rounded-xl bg-background-100 p-5">
                    <h4 className="font-semibold text-foreground-900 text-sm mb-2">
                      What if I make a mistake in the spreadsheet?
                    </h4>
                    <p className="text-foreground-500 text-xs leading-relaxed">
                      Google Sheets saves version history automatically. Go to File → Version History to restore any previous version. You cannot permanently break anything.
                    </p>
                  </div>
                  <div className="rounded-xl bg-background-100 p-5">
                    <h4 className="font-semibold text-foreground-900 text-sm mb-2">
                      How long until I see real savings?
                    </h4>
                    <p className="text-foreground-500 text-xs leading-relaxed">
                      Most beginners see shipping savings on their second or third order, once they start using the consolidation planner and carrier comparison features. The budget tracker provides immediate visibility into spending patterns.
                    </p>
                  </div>
                </div>
              </section>

              <section className="mt-12 pt-8 border-t border-background-200">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4">
                  Ready to Start Your Superbuy Spreadsheet Journey?
                </h2>
                <p>
                  Thousands of shoppers started exactly where you are now — curious but unsure, excited but a little intimidated. The Superbuy Spreadsheet system is built to grow with you. Your first week, it is a simple shopping list. By month three, it becomes a complete command center for every international purchase you make.
                </p>
                <p>
                  Explore our{' '}
                  <Link to="/article/superbuy-spreadsheet-template-guide" className="text-primary-500 hover:text-primary-600 font-medium underline underline-offset-2">
                    template library
                  </Link>{' '}
                  or check out{' '}
                  <Link to="/article/superbuy-spreadsheet-shipping-guide" className="text-primary-500 hover:text-primary-600 font-medium underline underline-offset-2">
                    advanced shipping strategies
                  </Link>. And don't forget to browse all shopping categories on{' '}
                  <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium underline underline-offset-2">
                    the homepage
                  </Link>.
                </p>
              </section>
            </div>

            <div className="mt-12 pt-8 border-t border-background-200">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <p className="text-xs text-foreground-400">Published by SuperbuySpread.hair Editorial Team</p>
                  <p className="text-xs text-foreground-400">Last updated: July 2026</p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <Link
                    to="/article/superbuy-spreadsheet-shipping-guide"
                    className="inline-flex items-center gap-2 px-4 py-2 border border-foreground-200 text-foreground-600 text-xs font-semibold rounded-full hover:bg-foreground-50 transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    <span>Shipping Guide</span>
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-xs"></i>
                    </div>
                  </Link>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-background-50 text-xs font-semibold rounded-full hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-left-line text-xs"></i>
                    </div>
                    <span>Back to Homepage</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}